import { isAllEventsLoadedSelector } from '@/store/selectors/events';
import { useAppSelector } from './useAppSelector';
import { useEffect, useRef } from 'react';

const iframeRootClass = 'embed-listing-item__root';

export const useResizeListingItems = () => {
  const isAllEventsLoaded = useAppSelector(isAllEventsLoadedSelector);

  const interval = useRef<ReturnType<typeof setTimeout> | null | number>(null);

  const getIframes = () => {
    return document.querySelectorAll(
      '.embed_frame',
    ) as NodeListOf<HTMLIFrameElement>;
  };

  const resize = (
    iframes: NodeListOf<HTMLIFrameElement>,
    maxHeight: number,
  ) => {
    iframes.forEach((frame) => {
      frame.style.height = `${maxHeight}px`;

      const root = frame.contentWindow?.document.getElementById('root');

      if (!root) return;

      root.classList.add(iframeRootClass);
    });
  };

  const resizeListingItems = (onSuccess?: () => void) => {
    /**
     * Remove class from root element in iframe if exist
     * Removing class minimizing the content height - this way
     * height calculation can be accurate
     *
     * On first time this does nothing
     */
    getIframes().forEach((frame) => {
      const root = frame.contentWindow?.document.getElementById('root');

      if (!root) return;

      root.classList.remove(iframeRootClass);
    });

    // Get fresh iframe objects just in case
    const iframes = getIframes();

    const heights: (number | undefined)[] = [];

    iframes.forEach((frame) => {
      // Take height of the card the holds iframe
      const listingItemHeight = frame.parentElement?.offsetHeight || 0;

      const body = frame.contentWindow?.document?.body;

      /**
       * Calculation strategy:
       *
       * Height = offsetHeight by default
       *
       * if scrollHeight is bigger then listingItemHeight that means that
       * card was smaller but after resize content of the card(iframe) became bigger
       * so we need to increase height.
       *
       * if scrollHeight is not bigger then listingItemHeight that means that
       * all content is visible by it's possible that iframe content is smaller then
       * card height. So it leads to either same height or smaller one, that gives us
       * fit height.
       */
      const scrollHeight = body?.scrollHeight || 0;
      const offsetHeight = body?.offsetHeight || 0;

      let height = offsetHeight;

      if (scrollHeight > listingItemHeight) {
        height = scrollHeight;
      }

      heights.push(height);
    });

    const allHasHeight = heights.every(Boolean);

    if (iframes.length && allHasHeight) {
      onSuccess?.();

      const maxHeight = Math.max(...(heights as number[]));

      resize(iframes, maxHeight);
    }
  };

  const onWindowResize = () => {
    resizeListingItems();
  };

  const onListingItemsResize = () => {
    clearInterval(interval.current);
    window.addEventListener('resize', onWindowResize);
  };

  useEffect(() => {
    if (!isAllEventsLoaded) {
      return;
    }

    // This way window resize event listener will be added once cards have been loaded and resized
    interval.current = window.setInterval(
      () => resizeListingItems(onListingItemsResize),
      300,
    );
  }, [isAllEventsLoaded]);

  useEffect(() => {
    return () => {
      clearInterval(interval.current);

      window.removeEventListener('resize', onWindowResize);
    };
  }, []);
};
